import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, Loader2, ExternalLink, Check } from 'lucide-react';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { useSubscription } from '@/hooks/useSubscription';
import Card from '@/components/ui/Card';
import DocumentHead from '@/components/DocumentHead';

const STATUS_KEYS: Record<string, string> = {
  active: 'billing.statusActive',
  trialing: 'billing.statusTrialing',
  past_due: 'billing.statusPastDue',
  canceled: 'billing.statusCanceled',
  cancelled: 'billing.statusCanceled',
};

interface PlanOption {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  interval: string;
  price: string | number;
  features: string[] | null;
  tier_level: number;
  billing_enabled: boolean;
}

function usePlans() {
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => api.get<{ plans: PlanOption[] }>('/subscription-plans'),
  });
}

export default function Billing() {
  const { t } = useTranslation('account');
  const addToast = useUIStore((s) => s.addToast);
  const { data, isLoading } = useSubscription();
  const { data: plansData, isLoading: plansLoading } = usePlans();
  const [openingPortal, setOpeningPortal] = useState(false);
  const [subscribingId, setSubscribingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const subscription = data?.subscription ?? null;
  const plans = (plansData?.plans ?? []).filter((p) => Number(p.price) > 0);
  const autoStarted = useRef(false);

  const fmtDate = (iso: string | null | undefined): string => {
    if (!iso) return t('billing.dateUnavailable');
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await api.post<{ portal_url?: string }>('/user/subscription/portal', {});
      if (res.portal_url) window.location.href = res.portal_url;
      else addToast(t('billing.manageError'), 'error');
    } catch (err: any) {
      addToast(err?.message || t('billing.manageError'), 'error');
    } finally {
      setOpeningPortal(false);
    }
  };

  const subscribe = async (plan: PlanOption) => {
    if (!plan.billing_enabled) return;
    setSubscribingId(plan.id);
    try {
      const origin = window.location.origin;
      const res = await api.post<{ checkout_url?: string }>('/user/subscription/checkout', {
        plan_id: plan.id,
        success_url: `${origin}/account/billing?subscribed=1`,
        cancel_url: `${origin}/account/billing`,
      });
      if (res.checkout_url) window.location.href = res.checkout_url;
      else addToast(t('billing.subscribeError'), 'error');
    } catch (err: any) {
      addToast(err?.message || t('billing.subscribeError'), 'error');
    } finally {
      setSubscribingId(null);
    }
  };

  // Success toast after returning from Stripe Checkout.
  useEffect(() => {
    if (searchParams.get('subscribed') === '1') {
      addToast(t('billing.subscribedToast'), 'success');
      searchParams.delete('subscribed');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link ?plan=<slug> (e.g. from an app paywall): auto-start checkout for
  // a logged-in customer with no active subscription, once plans have loaded.
  useEffect(() => {
    const wanted = searchParams.get('plan');
    if (!wanted || autoStarted.current) return;
    if (isLoading || plansLoading || subscription) return;
    const match = plans.find((p) => p.slug === wanted && p.billing_enabled);
    if (match) {
      autoStarted.current = true;
      void subscribe(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoading, plansLoading, subscription, plansData]);

  if (isLoading) {
    return <div className="text-ink-muted">{t('billing.loading')}</div>;
  }

  // No active subscription → show the plan picker.
  if (!subscription) {
    return (
      <div className="space-y-6 max-w-3xl">
        <DocumentHead title={t('billing.title')} />
        <div>
          <h2 className="text-xl font-display font-bold text-ink">{t('billing.title')}</h2>
          <p className="mt-1 text-ink-soft">{t('billing.choosePlanHelp')}</p>
        </div>

        {plansLoading ? (
          <div className="text-ink-muted">{t('billing.loading')}</div>
        ) : plans.length === 0 ? (
          <Card className="max-w-2xl text-center">
            <p className="text-ink-soft">{t('billing.noSubscription')}</p>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <Card key={plan.id} className="flex flex-col">
                <h3 className="text-lg font-bold text-ink">{plan.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-ink">
                    {t('billing.pricePerInterval', { amount: Number(plan.price).toFixed(0) })}
                  </span>
                  <span className="text-sm text-ink-muted">
                    {t('billing.perInterval', { interval: plan.interval })}
                  </span>
                </div>
                {plan.description && (
                  <p className="mt-2 text-sm text-ink-soft">{plan.description}</p>
                )}
                {Array.isArray(plan.features) && plan.features.length > 0 && (
                  <ul className="mt-4 space-y-1.5 text-sm text-ink-soft flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 mt-0.5 text-sage-600 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  onClick={() => subscribe(plan)}
                  disabled={!plan.billing_enabled || subscribingId === plan.id}
                  className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-ink text-surface hover:bg-ink-soft transition-colors disabled:opacity-50"
                >
                  {subscribingId === plan.id && <Loader2 className="w-4 h-4 animate-spin" />}
                  {plan.billing_enabled ? t('billing.subscribe') : t('billing.planComingSoon')}
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  const statusKey = STATUS_KEYS[subscription.status];
  const statusLabel = statusKey ? t(statusKey) : subscription.status;
  const periodLabel = subscription.cancel_at_period_end
    ? t('billing.cancelsOn')
    : t('billing.renewsOn');

  return (
    <div className="space-y-6 max-w-2xl">
      <DocumentHead title={t('billing.title')} />
      <h2 className="text-xl font-display font-bold text-ink">{t('billing.title')}</h2>

      <Card>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-sage-700 font-semibold">
              {statusLabel}
            </p>
            <h3 className="text-2xl font-bold text-ink">{subscription.plan_name}</h3>
          </div>
          {subscription.plan_price != null && subscription.plan_interval && (
            <div className="text-right text-ink-soft">
              <span className="text-2xl font-bold text-ink">
                {t('billing.pricePerInterval', {
                  amount: Number(subscription.plan_price).toFixed(0),
                })}
              </span>
              <span className="ml-1 text-sm">
                {t('billing.perInterval', { interval: subscription.plan_interval })}
              </span>
            </div>
          )}
        </div>

        <dl className="space-y-2 text-sm border-t border-line pt-4">
          <div className="flex justify-between">
            <dt className="text-ink-soft">{t('billing.status')}</dt>
            <dd className="text-ink font-medium">{statusLabel}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-soft">{periodLabel}</dt>
            <dd className="text-ink font-medium">{fmtDate(subscription.current_period_end)}</dd>
          </div>
        </dl>
      </Card>

      {subscription.can_manage ? (
        <div className="space-y-2">
          <button
            onClick={openPortal}
            disabled={openingPortal}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-line-strong rounded-md hover:bg-surface-soft transition-colors disabled:opacity-50"
          >
            {openingPortal ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ExternalLink className="w-4 h-4" />
            )}
            {t('billing.manage')}
          </button>
          <p className="text-xs text-ink-muted">{t('billing.manageHelp')}</p>
        </div>
      ) : (
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <CreditCard className="w-4 h-4 shrink-0" aria-hidden="true" />
          {t('billing.manageUnavailable')}
        </p>
      )}
    </div>
  );
}
